import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { siteConfig } from '@/config/site';

interface FaqItem {
    question: string;
    answer: string;
}

interface FaqCategory {
    name: string;
    items: FaqItem[];
}

const faqCategories: FaqCategory[] = [
    {
        name: 'General',
        items: [
            {
                question: `What is ${siteConfig.brand.name}?`,
                answer: `${siteConfig.brand.name} is a proprietary trading firm that provides talented traders with access to significant trading capital. We evaluate traders through our funding programs and offer successful candidates the opportunity to trade with our capital while keeping a generous share of the profits.`,
            },
            {
                question: 'How does prop trading work?',
                answer: 'Prop trading (proprietary trading) allows you to trade with the firm\'s capital instead of your own. You go through an evaluation process to demonstrate your trading skills, and once funded, you trade our capital under predefined risk parameters. Profits are split between you and the firm.',
            },
            {
                question: 'Is this real money?',
                answer: `During the evaluation phase, you trade on a simulated account with real market conditions. Once you pass the evaluation and become a funded trader, you trade with real capital provided by ${siteConfig.brand.name}. Your profit share payouts are real money sent directly to your preferred payment method.`,
            },
        ],
    },
    {
        name: 'Funding',
        items: [
            {
                question: 'What programs are available?',
                answer: `${siteConfig.brand.name} offers multiple funding programs to suit different trading styles and experience levels. Our programs range from $10,000 to $200,000 in trading capital, each with clear evaluation criteria and scaling opportunities for consistent performers.`,
            },
            {
                question: "What's the profit split?",
                answer: 'Our funded traders enjoy up to an 90% profit split. The exact split depends on your program tier and performance history. As you demonstrate consistent profitability and scale your account, your profit share can increase.',
            },
            {
                question: 'How do payouts work?',
                answer: 'Payouts are processed on a regular schedule, typically bi-weekly. Once you meet the minimum payout threshold, you can request a withdrawal through your dashboard. We support multiple payment methods including bank transfers and cryptocurrency for fast, global payouts.',
            },
        ],
    },
    {
        name: 'Trading',
        items: [
            {
                question: 'What platforms do you support?',
                answer: `${siteConfig.brand.name} primarily supports the ${siteConfig.platform.name} platform, renowned for its advanced charting, fast execution, and professional-grade tools. ${siteConfig.platform.name} is available on desktop, web, and mobile, allowing you to trade from anywhere.`,
            },
            {
                question: 'What instruments can I trade?',
                answer: 'You can trade a wide range of instruments including major, minor, and exotic forex pairs, indices (US30, NAS100, SPX500), commodities (gold, silver, oil), and select cryptocurrencies. The full instrument list is available in your trading platform.',
            },
            {
                question: 'Are there trading restrictions?',
                answer: 'We maintain fair and transparent trading rules. There are daily and overall drawdown limits to manage risk, but we do not restrict trading styles. You are free to use EAs, trade during news events, hold positions overnight, and trade on weekends where markets are open.',
            },
        ],
    },
    {
        name: 'Account',
        items: [
            {
                question: 'How do I get started?',
                answer: 'Getting started is simple. Choose a funding program that matches your goals, complete the registration, and start your evaluation. Once you pass the evaluation by meeting the profit targets within the risk parameters, you receive a funded account.',
            },
            {
                question: 'What happens if I fail?',
                answer: 'If you breach the drawdown rules or fail to meet the evaluation criteria, your evaluation account will be closed. However, this is not the end — many of our most successful traders did not pass on their first attempt. You can purchase a new evaluation at any time.',
            },
            {
                question: 'Can I retry?',
                answer: 'Absolutely. There is no limit to the number of times you can attempt our evaluation. We also offer discounted retries and occasional promotions to help traders get back on track. Every attempt is a learning opportunity.',
            },
        ],
    },
];

function FaqAccordionItem({ item }: { item: FaqItem }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border border-border rounded-xl overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex w-full items-center justify-between p-5 text-left bg-card hover:bg-accent transition-colors duration-200 cursor-pointer"
            >
                <span className="text-base font-medium text-foreground pr-4">{item.question}</span>
                <ChevronDown
                    className={`h-5 w-5 shrink-0 text-primary transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-5 bg-card/80">
                            <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function Faq() {
    const [activeCategory, setActiveCategory] = useState('General');

    const currentCategory = faqCategories.find((c) => c.name === activeCategory) || faqCategories[0];

    return (
        <AppLayout title={`FAQ - ${siteConfig.brand.name}`} description={`Find answers to frequently asked questions about ${siteConfig.brand.name} prop trading.`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center">
                    <FadeIn>
                        <Badge className="mb-6">Support</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Frequently Asked <span className="text-gradient">Questions</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            Find answers to common questions about our prop trading programs, payouts, and platform.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Category Tabs + FAQ Items */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="flex flex-wrap justify-center gap-3 mb-12">
                        {faqCategories.map((category) => (
                            <button
                                key={category.name}
                                onClick={() => setActiveCategory(category.name)}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                                    activeCategory === category.name
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-secondary-soft text-muted-foreground hover:text-foreground hover:bg-accent border border-border'
                                }`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                </FadeIn>

                <StaggerChildren className="max-w-3xl mx-auto space-y-3">
                    {currentCategory.items.map((item, index) => (
                        <motion.div key={`${activeCategory}-${index}`} variants={staggerItem}>
                            <FaqAccordionItem item={item} />
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-background">
                <FadeIn>
                    <div className="text-center rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-12 lg:p-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Still have <span className="text-gradient">questions?</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                            Our support team is available 24/5 to help you with any questions or concerns.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact">
                                <Button size="lg">
                                    Contact Support
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Button size="lg" variant="outline">
                                Join Discord
                            </Button>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
